import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Package, Upload, Trash2, Download, Search, Filter, RefreshCw, CheckSquare, Square, Eye, Zap, Globe, AlertCircle, CheckCircle, Loader, FileBox, MoreVertical, Copy, Grid, List, X, Info, HardDrive } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { uploadCompressedObjAsset, getCacheStats, clearAllAssetCache } from '../../services/objAssetService';

// ─── File type helpers ──────────────────────────────────────────────────────────
const getModelFileType = (filename) => {
  const n = filename?.toLowerCase();
  if (n?.endsWith('.glb')) return 'glb';
  if (n?.endsWith('.gltf')) return 'gltf';
  return 'obj';
};

const is3DModelFile = (file) => {
  const n = file?.name?.toLowerCase();
  return n?.endsWith('.obj') || n?.endsWith('.glb') || n?.endsWith('.gltf');
};

// ─── OBJ Parser ────────────────────────────────────────────────────────────────
const parseObjStats = (text) => {
  const lines = text?.split('\n') || [];
  let vertices = 0, faces = 0, objects = 0;
  const materials = [];
  for (const line of lines) {
    const t = line?.trim();
    if (t?.startsWith('v ')) vertices++;
    else if (t?.startsWith('f ')) faces++;
    else if (t?.startsWith('o ') || t?.startsWith('g ')) objects++;
    else if (t?.startsWith('usemtl ')) {
      const m = t?.replace('usemtl ', '')?.trim();
      if (m && !materials?.includes(m)) materials?.push(m);
    }
  }
  return { vertices, faces, objects: Math.max(objects, 1), materials };
};

const validateObjFile = (file) =>
  new Promise((resolve) => {
    if (!file.name.toLowerCase().endsWith('.obj')) {
      return resolve({ valid: false, error: 'Extension .obj requise' });
    }
    if (file.size > 200 * 1024 * 1024) {
      return resolve({ valid: false, error: 'Fichier trop volumineux (max 200 MB)' });
    }
    if (file.size === 0) {
      return resolve({ valid: false, error: 'Fichier vide' });
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result || '';
      if (!/^v\s/m.test(text)) {
        return resolve({ valid: false, error: 'Aucun sommet (vertex) détecté' });
      }
      resolve({ valid: true, stats: parseObjStats(text) });
    };
    reader.onerror = () => resolve({ valid: false, error: 'Lecture impossible' });
    reader.readAsText(file.slice(0, 51200));
  });

const validateGlbFile = (file) =>
  new Promise((resolve) => {
    const nameLower = file.name.toLowerCase();
    if (!nameLower.endsWith('.glb') && !nameLower.endsWith('.gltf')) {
      return resolve({ valid: false, error: 'Extension .glb ou .gltf requise' });
    }
    if (file.size > 500 * 1024 * 1024) {
      return resolve({ valid: false, error: 'Fichier trop volumineux (max 500 MB)' });
    }
    if (file.size === 0) {
      return resolve({ valid: false, error: 'Fichier vide' });
    }
    if (nameLower.endsWith('.glb')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buf = e.target?.result;
        const view = new DataView(buf);
        const magic = view.getUint32(0, true);
        if (magic !== 0x46546C67) {
          return resolve({ valid: false, error: 'Fichier GLB invalide: signature binaire incorrecte' });
        }
        const version = view.getUint32(4, true);
        resolve({ valid: true, stats: { format: 'GLB', version } });
      };
      reader.onerror = () => resolve({ valid: false, error: 'Lecture impossible' });
      reader.readAsArrayBuffer(file.slice(0, 12));
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result || '{}');
          if (!json.asset?.version) {
            return resolve({ valid: false, error: 'Fichier GLTF invalide: champ asset.version manquant' });
          }
          resolve({ valid: true, stats: { format: 'GLTF', version: json.asset.version, meshes: json.meshes?.length || 0 } });
        } catch {
          resolve({ valid: false, error: 'Fichier GLTF invalide: JSON malformé' });
        }
      };
      reader.onerror = () => resolve({ valid: false, error: 'Lecture impossible' });
      reader.readAsText(file.slice(0, 4096));
    }
  });

// Sanitize filename: normalize accents, remove special chars, replace spaces with underscores
const sanitizeFileName = (name) => {
  return name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-zA-Z0-9._-]/g, '_')?.replace(/_+/g, '_')?.replace(/^_|_$/g, '');
};

const validate3DFile = (file) => {
  const type = getModelFileType(file?.name);
  if (type === 'obj') return validateObjFile(file);
  return validateGlbFile(file);
};

const formatBytes = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024)?.toFixed(1)} KB`;
  return `${(b / 1048576)?.toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Asset Card ────────────────────────────────────────────────────────────────
const AssetCard = ({ asset, selected, onSelect, onDelete, onPreview, onCopyUrl, onDownload }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (menuRef?.current && !menuRef?.current?.contains(e?.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isGlb = asset?.type === 'glb' || asset?.type === 'gltf' || asset?.name?.toLowerCase()?.endsWith('.glb') || asset?.name?.toLowerCase()?.endsWith('.gltf');
  const typeLabel = isGlb ? (asset?.name?.toLowerCase()?.endsWith('.gltf') ? '.GLTF' : '.GLB') : '.OBJ';
  const typeBadgeColor = isGlb ? 'text-emerald-400' : 'text-brand-gold';

  return (
    <div className={`group relative bg-white rounded-2xl border-2 transition-all duration-200 overflow-hidden ${selected ? 'border-brand-gold shadow-lg' : 'border-border hover:border-brand-gold/40 hover:shadow-md'}`}>
      {/* Checkbox */}
      <button onClick={() => onSelect(asset?.id)} className="absolute top-3 left-3 z-10">
        {selected
          ? <CheckSquare size={18} className="text-brand-gold" />
          : <Square size={18} className="text-gray-300 group-hover:text-gray-400" />}
      </button>
      {/* Preview area */}
      <div
        onClick={() => onPreview(asset)}
        className="h-36 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center cursor-pointer relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: isGlb
            ? 'radial-gradient(circle at 30% 40%, #10b981 0%, transparent 60%), radial-gradient(circle at 70% 70%, #059669 0%, transparent 50%)'
            : 'radial-gradient(circle at 30% 40%, #d4a843 0%, transparent 60%), radial-gradient(circle at 70% 70%, #8b6914 0%, transparent 50%)' }} />
        <div className="flex flex-col items-center gap-2 z-10">
          <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
            <Package size={28} className={typeBadgeColor} />
          </div>
          <span className={`text-xs font-mono ${typeBadgeColor}`}>{typeLabel}</span>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="bg-white/90 rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-800">
            <Eye size={13} /> Aperçu
          </div>
        </div>
      </div>
      {/* Info */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text-primary truncate">{asset?.name}</p>
            <p className="text-xs text-text-secondary mt-0.5 truncate">{asset?.property_title || 'Sans propriété'}</p>
          </div>
          <div ref={menuRef} className="relative flex-shrink-0">
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 hover:bg-surface rounded-lg transition-colors">
              <MoreVertical size={15} className="text-text-secondary" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 bg-white border border-border rounded-xl shadow-lg z-20 w-44 py-1 text-sm">
                <button onClick={() => { onPreview(asset); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-surface text-text-primary">
                  <Eye size={14} /> Aperçu 3D
                </button>
                <button onClick={() => { onCopyUrl(asset); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-surface text-text-primary">
                  <Copy size={14} /> Copier URL CDN
                </button>
                <button onClick={() => { onDownload(asset); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-surface text-text-primary">
                  <Download size={14} /> Télécharger
                </button>
                <hr className="my-1 border-border" />
                <button onClick={() => { onDelete(asset?.id); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-red-50 text-red-600">
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats chips */}
        {asset?.stats && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {asset?.stats?.vertices !== undefined ? (
              <>
                <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-mono">{asset?.stats?.vertices?.toLocaleString()}v</span>
                <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-mono">{asset?.stats?.faces?.toLocaleString()}f</span>
              </>
            ) : (
              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-mono">{asset?.stats?.format} v{asset?.stats?.version}</span>
            )}
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{formatBytes(asset?.size)}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-text-secondary">{formatDate(asset?.uploaded_at)}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${asset?.cdn_optimized ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
            {asset?.cdn_optimized ? '⚡ CDN' : '○ Local'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Asset Row (List View) ──────────────────────────────────────────────────────
const AssetRow = ({ asset, selected, onSelect, onDelete, onPreview, onCopyUrl, onDownload }) => {
  const isGlb = asset?.type === 'glb' || asset?.type === 'gltf' || asset?.name?.toLowerCase()?.endsWith('.glb') || asset?.name?.toLowerCase()?.endsWith('.gltf');
  const typeLabel = isGlb ? (asset?.name?.toLowerCase()?.endsWith('.gltf') ? 'GLTF' : 'GLB') : 'OBJ';

  return (
    <tr className={`border-b border-border/50 hover:bg-surface/50 transition-colors ${selected ? 'bg-brand-gold/5' : ''}`}>
      <td className="px-4 py-3">
        <button onClick={() => onSelect(asset?.id)}>
          {selected ? <CheckSquare size={16} className="text-brand-gold" /> : <Square size={16} className="text-gray-300" />}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package size={16} className={isGlb ? 'text-emerald-400' : 'text-brand-gold'} />
          </div>
          <div>
            <p className="text-sm font-medium text-text-primary">{asset?.name}</p>
            <p className="text-xs text-text-secondary">{formatBytes(asset?.size)} · <span className={`font-mono font-bold ${isGlb ? 'text-emerald-600' : 'text-indigo-600'}`}>{typeLabel}</span></p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">{asset?.property_title || '—'}</td>
      <td className="px-4 py-3">
        {asset?.stats ? (
          <div className="flex items-center gap-1.5">
            {asset?.stats?.vertices !== undefined ? (
              <>
                <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-mono">{asset?.stats?.vertices?.toLocaleString()}v</span>
                <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-mono">{asset?.stats?.faces?.toLocaleString()}f</span>
              </>
            ) : (
              <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">{asset?.stats?.format}</span>
            )}
          </div>
        ) : <span className="text-xs text-text-secondary">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-text-secondary">{formatDate(asset?.uploaded_at)}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${asset?.cdn_optimized ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {asset?.cdn_optimized ? '⚡ CDN' : '○ Local'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button onClick={() => onPreview(asset)} className="p-1.5 hover:bg-surface rounded-lg transition-colors" title="Aperçu">
            <Eye size={14} className="text-text-secondary" />
          </button>
          <button onClick={() => onCopyUrl(asset)} className="p-1.5 hover:bg-surface rounded-lg transition-colors" title="Copier URL CDN">
            <Copy size={14} className="text-text-secondary" />
          </button>
          <button onClick={() => onDownload(asset)} className="p-1.5 hover:bg-surface rounded-lg transition-colors" title="Télécharger">
            <Download size={14} className="text-text-secondary" />
          </button>
          <button onClick={() => onDelete(asset?.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
            <Trash2 size={14} className="text-red-400" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Preview Modal ──────────────────────────────────────────────────────────────
const PreviewModal = ({ asset, onClose }) => {
  if (!asset) return null;
  const isGlb = asset?.type === 'glb' || asset?.type === 'gltf' || asset?.name?.toLowerCase()?.endsWith('.glb') || asset?.name?.toLowerCase()?.endsWith('.gltf');
  const typeLabel = isGlb ? (asset?.name?.toLowerCase()?.endsWith('.gltf') ? 'GLTF' : 'GLB') : 'OBJ';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-slate-800 rounded-xl flex items-center justify-center">
              <Package size={18} className={isGlb ? 'text-emerald-400' : 'text-brand-gold'} />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">{asset?.name}</h3>
              <p className="text-xs text-text-secondary">{asset?.property_title || 'Sans propriété'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-xl transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* 3D Viewer placeholder */}
          <div className="h-64 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20"
              style={{ backgroundImage: isGlb
                ? 'radial-gradient(circle at 30% 40%, #10b981 0%, transparent 60%)'
                : 'radial-gradient(circle at 30% 40%, #d4a843 0%, transparent 60%)' }} />
            <div className="text-center z-10">
              <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-3">
                <Package size={36} className={isGlb ? 'text-emerald-400' : 'text-brand-gold'} />
              </div>
              <p className="text-white/80 text-sm font-medium">Modèle 3D {typeLabel}</p>
              <p className="text-white/40 text-xs mt-1">Rendu interactif disponible sur la page propriété</p>
            </div>
          </div>

          {/* Stats grid */}
          {asset?.stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(
  asset?.stats?.vertices !== undefined
    ? [
        {
          label: 'Sommets',
          value: asset?.stats?.vertices?.toLocaleString(),
          color: 'text-indigo-600',
          bg: 'bg-indigo-50',
        },
        {
          label: 'Faces',
          value: asset?.stats?.faces?.toLocaleString(),
          color: 'text-purple-600',
          bg: 'bg-purple-50',
        },
        {
          label: 'Objets',
          value: asset?.stats?.objects,
          color: 'text-blue-600',
          bg: 'bg-blue-50',
        },
        {
          label: 'Matériaux',
          value: asset?.stats?.materials?.length || 0,
          color: 'text-amber-600',
          bg: 'bg-amber-50',
        },
      ]
    : [
        {
          label: 'Format',
          value: asset?.stats?.format,
          color: 'text-emerald-600',
          bg: 'bg-emerald-50',
        },
        {
          label: 'Version',
          value: `v${asset?.stats?.version}`,
          color: 'text-teal-600',
          bg: 'bg-teal-50',
        },
        {
          label: 'Maillages',
          value: asset?.stats?.meshes ?? '—',
          color: 'text-blue-600',
          bg: 'bg-blue-50',
        },
        {
          label: 'Taille',
          value: formatBytes(asset?.size),
          color: 'text-amber-600',
          bg: 'bg-amber-50',
        },
      ]
).map((s) => (
  <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
    <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
    <div className="text-xs text-text-secondary mt-0.5">
      {s.label}
    </div>
  </div>
))}
            </div>
          )}

          {/* Metadata */}
          <div className="bg-surface rounded-xl p-4 space-y-2.5">
            <h4 className="text-sm font-semibold text-text-primary mb-3">Métadonnées</h4>
            {[
              { label: 'Fichier', value: asset?.name },
              { label: 'Format', value: typeLabel },
              { label: 'Taille', value: formatBytes(asset?.size) },
              { label: 'Propriété', value: asset?.property_title || '—' },
              { label: 'Importé le', value: formatDate(asset?.uploaded_at) },
              { label: 'CDN', value: asset?.cdn_optimized ? '✓ Optimisé' : '○ Non optimisé' },
            ]?.map((m) => (
              <div key={m?.label} className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">{m?.label}</span>
                <span className="text-text-primary font-medium">{m?.value}</span>
              </div>
            ))}
          </div>

          {/* URL */}
          {asset?.url && (
            <div className="bg-surface rounded-xl p-4">
              <h4 className="text-sm font-semibold text-text-primary mb-2">URL CDN</h4>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-border rounded-lg px-3 py-2 text-text-secondary truncate">{asset?.url}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(asset?.url); }}
                  className="flex-shrink-0 p-2 bg-brand-gold/10 hover:bg-brand-gold/20 rounded-lg transition-colors"
                >
                  <Copy size={14} className="text-brand-gold" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Upload Modal ───────────────────────────────────────────────────────────────
const UploadModal = ({ isOpen, onClose, properties, onUploaded }) => {
  const [fileEntries, setFileEntries] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!isOpen) { setFileEntries([]); setMessage(null); setIsUploading(false); setSelectedPropertyId(''); }
  }, [isOpen]);

  const processFiles = useCallback(async (files) => {
    const modelFiles = Array.from(files)?.filter(f => is3DModelFile(f));
    if (!modelFiles?.length) { setMessage({ type: 'error', text: 'Aucun fichier 3D détecté (.obj, .glb, .gltf)' }); return; }
    const entries = modelFiles?.map(file => ({
      id: `${file?.name}_${Date.now()}_${Math.random()}`,
      file,
      fileType: getModelFileType(file?.name),
      status: 'validating',
      stats: null,
      error: null,
    }));
    setFileEntries(prev => [...prev, ...entries]);
    setMessage(null);
    for (const entry of entries) {
      const result = await validate3DFile(entry?.file);
      setFileEntries(prev => prev?.map(e => e?.id === entry?.id ? { ...e, status: result?.valid ? 'valid' : 'invalid', stats: result?.stats || null, error: result?.error || null } : e));
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e?.preventDefault(); e?.stopPropagation();
    dragCounter.current = 0; setIsDragging(false);
    processFiles(e?.dataTransfer?.files);
  }, [processFiles]);

  const handleDragEnter = useCallback((e) => { e?.preventDefault(); dragCounter.current++; setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e?.preventDefault(); dragCounter.current--; if (dragCounter?.current === 0) setIsDragging(false); }, []);
  const handleDragOver = useCallback((e) => { e?.preventDefault(); }, []);

  const validEntries = fileEntries?.filter(e => e?.status === 'valid');

  const handleUpload = async () => {
    if (!validEntries?.length) return;
    setIsUploading(true);
    setMessage(null);
    const uploaded = [];
    for (const entry of validEntries) {
      setFileEntries(prev => prev?.map(e => e?.id === entry?.id ? { ...e, status: 'uploading' } : e));
      try {
        const folder = entry?.fileType === 'obj' ? 'obj-models' : 'glb-models';
        const path = `${folder}/${selectedPropertyId || 'unassigned'}/${Date.now()}_${sanitizeFileName(entry?.file?.name)}`;
        let publicUrl;
        let compressionStats = null;

        if (entry?.fileType === 'obj') {
          const result = await uploadCompressedObjAsset(entry?.file, path, {
            onProgress: (pct) => {
              setFileEntries(prev => prev?.map(e => e?.id === entry?.id ? { ...e, progress: pct } : e));
            },
          });
          publicUrl = result?.publicUrl;
          compressionStats = result?.compressionStats;
        } else {
          const { data, error } = await supabase?.storage
            ?.from('property-assets')
            ?.upload(path, entry?.file, {
              cacheControl: '31536000',
              upsert: false,
              contentType: entry?.fileType === 'glb' ? 'model/gltf-binary' : 'model/gltf+json',
            });
          if (error) throw error;
          const { data: urlData } = supabase?.storage?.from('property-assets')?.getPublicUrl(path);
          publicUrl = urlData?.publicUrl;
          compressionStats = { compressedSize: entry?.file?.size, originalSize: entry?.file?.size, ratio: 0 };
        }

        setFileEntries(prev => prev?.map(e => e?.id === entry?.id ? { ...e, status: 'uploaded', url: publicUrl } : e));
        uploaded?.push({
          name: entry?.file?.name,
          url: publicUrl,
          type: entry?.fileType,
          size: compressionStats?.compressedSize,
          originalSize: compressionStats?.originalSize,
          compressionRatio: compressionStats?.ratio,
          stats: entry?.stats,
          property_id: selectedPropertyId || null,
          uploaded_at: new Date()?.toISOString(),
          cdn_optimized: true,
          version: 'v1',
        });
      } catch (err) {
        setFileEntries(prev => prev?.map(e => e?.id === entry?.id ? { ...e, status: 'error', error: err?.message } : e));
      }
    }
    if (uploaded?.length) {
      const glbCount = uploaded?.filter(u => u?.type === 'glb' || u?.type === 'gltf')?.length;
      const objCount = uploaded?.filter(u => u?.type === 'obj')?.length;
      const parts = [];
      if (objCount > 0) parts?.push(`${objCount} .obj`);
      if (glbCount > 0) parts?.push(`${glbCount} .glb/.gltf`);
      setMessage({ type: 'success', text: `${parts?.join(' + ')} importé(s) avec succès` });
      if (onUploaded) onUploaded(uploaded);
    }
    setIsUploading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Upload size={18} className="text-indigo-600" />
            </div>
            <h2 className="font-semibold text-text-primary">Importer des modèles 3D (.obj / .glb)</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-xl transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Property selector */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Associer à une propriété (optionnel)</label>
            <select
              value={selectedPropertyId}
              onChange={e => setSelectedPropertyId(e?.target?.value)}
              className="w-full px-3 py-2.5 border border-border rounded-xl text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            >
              <option value="">— Sans propriété —</option>
              {properties?.map(p => <option key={p?.id} value={p?.id}>{p?.title}</option>)}
            </select>
          </div>

          {/* Drop zone */}
          <div
            onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
            onDragOver={handleDragOver} onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${isDragging ? 'border-brand-gold bg-brand-gold/5 scale-[1.01]' : 'border-border bg-surface hover:border-brand-gold/50 hover:bg-brand-gold/5'}`}
          >
            <input ref={fileInputRef} type="file" accept=".obj,.glb,.gltf" multiple className="hidden" onChange={e => { if (e?.target?.files?.length) { processFiles(e?.target?.files); e.target.value = ''; } }} />
            <div className="flex flex-col items-center gap-3">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${isDragging ? 'bg-brand-gold/20' : 'bg-white border border-border'}`}>
                <Upload size={24} className={isDragging ? 'text-brand-gold' : 'text-text-secondary'} />
              </div>
              <div>
                <p className="font-medium text-text-primary">{isDragging ? 'Déposez ici' : 'Glissez-déposez vos modèles 3D'}</p>
                <p className="text-sm text-text-secondary mt-1">ou <span className="text-brand-gold font-medium">parcourir</span></p>
              </div>
              <div className="flex items-center gap-2 text-xs text-text-secondary flex-wrap justify-center">
                <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono">.obj</span>
                <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-mono">.glb</span>
                <span className="bg-teal-50 text-teal-600 px-2 py-0.5 rounded font-mono">.gltf</span>
                <span>•</span><span>Max 500 MB</span><span>•</span><span>Multiple</span>
              </div>
            </div>
          </div>

          {message && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${message?.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {message?.type === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
              {message?.text}
            </div>
          )}

          {fileEntries?.length > 0 && (
            <div className="space-y-2">
              {fileEntries?.map(entry => {
                const isGlb = entry?.fileType === 'glb' || entry?.fileType === 'gltf';
                return (
                  <div key={entry?.id} className={`flex items-center gap-3 p-3 rounded-xl border ${entry?.status === 'valid' || entry?.status === 'uploaded' ? 'bg-emerald-50 border-emerald-200' : entry?.status === 'invalid' || entry?.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="w-8 h-8 bg-white rounded-lg border border-current/20 flex items-center justify-center flex-shrink-0">
                      <FileBox size={16} className={isGlb ? 'text-emerald-500' : 'text-indigo-500'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-text-primary truncate">{entry?.file?.name}</p>
                        <span className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${isGlb ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {entry?.fileType?.toUpperCase()}
                        </span>
                      </div>
                      {entry?.stats?.vertices !== undefined && <p className="text-xs text-text-secondary">{entry?.stats?.vertices?.toLocaleString()} sommets · {entry?.stats?.faces?.toLocaleString()} faces</p>}
                      {entry?.stats?.format && <p className="text-xs text-text-secondary">{entry?.stats?.format} v{entry?.stats?.version}</p>}
                      {entry?.error && <p className="text-xs text-red-600">{entry?.error}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {entry?.status === 'validating' || entry?.status === 'uploading' ? <Loader size={14} className="animate-spin text-blue-500" /> : entry?.status === 'valid' || entry?.status === 'uploaded' ? <CheckCircle size={14} className="text-emerald-500" /> : <AlertCircle size={14} className="text-red-500" />}
                      <span className="text-xs text-text-secondary capitalize">{entry?.status === 'validating' ? 'Validation...' : entry?.status === 'uploading' ? 'Envoi...' : entry?.status === 'valid' ? 'Valide' : entry?.status === 'uploaded' ? 'Importé' : entry?.status === 'invalid' ? 'Invalide' : 'Erreur'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-border flex items-center justify-between gap-3">
          <span className="text-sm text-text-secondary">{validEntries?.length > 0 ? `${validEntries?.length} fichier(s) prêt(s)` : ''}</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={isUploading} className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface hover:bg-border rounded-xl transition-colors">Annuler</button>
            <button
              onClick={handleUpload}
              disabled={!validEntries?.length || isUploading}
              className={`px-4 py-2 text-sm font-medium rounded-xl flex items-center gap-2 transition-colors ${validEntries?.length && !isUploading ? 'bg-brand-gold hover:bg-brand-earth text-white' : 'bg-surface text-text-secondary cursor-not-allowed'}`}
            >
              {isUploading ? <><Loader size={14} className="animate-spin" /> Import...</> : <><Upload size={14} /> Importer ({validEntries?.length})</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
const AssetManagementHub = () => {
  const [assets, setAssets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [search, setSearch] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterCdn, setFilterCdn] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [cacheStats, setCacheStats] = useState({ totalEntries: 0, memoryEntries: 0, oldestEntry: null });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Refresh cache stats
  const refreshCacheStats = useCallback(() => {
    setCacheStats(getCacheStats());
  }, []);

  const handleClearCache = () => {
    clearAllAssetCache();
    refreshCacheStats();
    showToast('Cache des assets 3D vidé');
  };

  // Load properties and build asset list from property videos
  const loadData = async () => {
    setLoading(true);
    try {
      const { data: props } = await supabase?.from('properties')?.select('id, title, videos')?.order('created_at', { ascending: false });
      setProperties(props || []);

      const allAssets = [];
      for (const prop of (props || [])) {
        const vids = Array.isArray(prop?.videos) ? prop?.videos : [];
        for (const v of vids) {
          const urlLower = v?.url?.toLowerCase()?.split('?')?.[0] || '';
          const isModel = v?.type === 'obj'|| v?.type === 'glb' || v?.type === 'gltf' || urlLower?.includes('.obj') || urlLower?.includes('.glb') || urlLower?.includes('.gltf');
          if (isModel) {
            allAssets?.push({
              id: v?.id || `${prop?.id}_${v?.url}`,
              name: v?.title || v?.url?.split('/')?.pop()?.split('?')?.[0] || 'model.obj',
              url: v?.url,
              type: v?.type || (urlLower?.endsWith('.glb') ? 'glb' : urlLower?.endsWith('.gltf') ? 'gltf' : 'obj'),
              property_id: prop?.id,
              property_title: prop?.title,
              uploaded_at: v?.added_at || null,
              size: v?.size || null,
              stats: v?.stats || null,
              cdn_optimized: !!(v?.url?.includes('supabase') || v?.url?.includes('cdn')),
              version: v?.version || 'v1',
            });
          }
        }
      }
      setAssets(allAssets);
    } catch (err) {
      console.error(err);
      showToast('Erreur lors du chargement des assets', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); refreshCacheStats(); }, []);

  // Filter + sort
  const filtered = assets?.filter(a => {
      const q = search?.toLowerCase();
      const matchSearch = !q || a?.name?.toLowerCase()?.includes(q) || a?.property_title?.toLowerCase()?.includes(q);
      const matchProp = !filterProperty || a?.property_id === filterProperty;
      const matchCdn = !filterCdn || (filterCdn === 'cdn' ? a?.cdn_optimized : !a?.cdn_optimized);
      return matchSearch && matchProp && matchCdn;
    })?.sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0);
      if (sortBy === 'date_asc') return new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0);
      if (sortBy === 'name_asc') return a?.name?.localeCompare(b?.name);
      if (sortBy === 'name_desc') return b?.name?.localeCompare(a?.name);
      if (sortBy === 'size_desc') return (b?.size || 0) - (a?.size || 0);
      return 0;
    });

  const allSelected = filtered?.length > 0 && filtered?.every(a => selectedIds?.includes(a?.id));

  const toggleSelect = (id) => setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : filtered?.map(a => a?.id));

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet asset ?')) return;
    // Remove from property videos
    const asset = assets?.find(a => a?.id === id);
    if (asset?.property_id) {
      const prop = properties?.find(p => p?.id === asset?.property_id);
      if (prop) {
        const updatedVideos = (prop?.videos || [])?.filter(v => v?.id !== id && v?.url !== asset?.url);
        await supabase?.from('properties')?.update({ videos: updatedVideos })?.eq('id', prop?.id);
      }
    }
    setAssets(prev => prev?.filter(a => a?.id !== id));
    setSelectedIds(prev => prev?.filter(x => x !== id));
    showToast('Asset supprimé');
  };

  const handleBatchDelete = async () => {
    if (!selectedIds?.length || !window.confirm(`Supprimer ${selectedIds?.length} asset(s) ?`)) return;
    setBatchLoading(true);
    for (const id of selectedIds) await handleDelete(id);
    setSelectedIds([]);
    setBatchLoading(false);
    showToast(`${selectedIds?.length} asset(s) supprimé(s)`);
  };

  const handleCopyUrl = (asset) => {
    navigator.clipboard?.writeText(asset?.url || '');
    showToast('URL CDN copiée dans le presse-papiers');
  };

  const handleDownload = (asset) => {
    if (asset?.url) { const a = document.createElement('a'); a.href = asset?.url; a.download = asset?.name; a?.click(); }
  };

  const handleBatchDownload = () => {
    const toDownload = assets?.filter(a => selectedIds?.includes(a?.id));
    toDownload?.forEach(a => handleDownload(a));
    showToast(`${toDownload?.length} téléchargement(s) lancé(s)`);
  };

  const handleBatchCdnOptimize = async () => {
    setBatchLoading(true);
    setAssets(prev => prev?.map(a => selectedIds?.includes(a?.id) ? { ...a, cdn_optimized: true } : a));
    showToast(`${selectedIds?.length} asset(s) marqué(s) CDN optimisé`);
    setSelectedIds([]);
    setBatchLoading(false);
  };

  const handleUploaded = (newAssets) => {
    const mapped = newAssets?.map(a => ({
      id: `new_${Date.now()}_${Math.random()}`,
      name: a?.name,
      url: a?.url,
      property_id: a?.property_id,
      property_title: properties?.find(p => p?.id === a?.property_id)?.title || null,
      uploaded_at: a?.uploaded_at,
      size: a?.size,
      stats: a?.stats,
      cdn_optimized: a?.cdn_optimized,
      version: a?.version,
    }));
    setAssets(prev => [...mapped, ...prev]);
    showToast(`${newAssets?.length} modèle(s) importé(s) avec succès`);
    loadData();
  };

  // Stats
  const totalSize = assets?.reduce((s, a) => s + (a?.size || 0), 0);
  const cdnCount = assets?.filter(a => a?.cdn_optimized)?.length;
  const totalVertices = assets?.reduce((s, a) => s + (a?.stats?.vertices || 0), 0);

  return (
    <>
      <Helmet>
        <title>Hub Assets 3D — Kapital States</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white border-b border-border sticky top-0 z-30">
          <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/property-management-dashboard" className="p-2 hover:bg-surface rounded-xl transition-colors">
                <Icon name="ArrowLeft" size={18} className="text-text-secondary" />
              </Link>
              <div className="w-9 h-9 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center">
                <Package size={18} color="white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-text-primary leading-tight">Hub Assets 3D</h1>
                <p className="text-xs text-text-secondary">Gestion · Compression · Cache · CDN</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearCache}
                className="p-2 hover:bg-surface rounded-xl transition-colors"
                title={`Vider le cache (${cacheStats?.totalEntries} entrées)`}
              >
                <Zap size={16} className="text-text-secondary" />
              </button>
              <button onClick={loadData} className="p-2 hover:bg-surface rounded-xl transition-colors" title="Actualiser">
                <RefreshCw size={16} className="text-text-secondary" />
              </button>
              <button
                onClick={() => setUploadOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gold hover:bg-brand-earth text-white text-sm font-medium rounded-xl transition-colors"
              >
                <Upload size={15} /> Importer .obj
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-6 py-6 space-y-6">
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: <Package size={18} className="text-brand-gold" />, label: 'Total Assets', value: assets?.length, bg: 'bg-brand-gold/10' },
              { icon: <Globe size={18} className="text-emerald-600" />, label: 'CDN Optimisés', value: cdnCount, bg: 'bg-emerald-50' },
              { icon: <HardDrive size={18} className="text-blue-600" />, label: 'Stockage Total', value: formatBytes(totalSize), bg: 'bg-blue-50' },
              { icon: <Zap size={18} className="text-purple-600" />, label: 'Cache Entrées', value: cacheStats?.totalEntries, bg: 'bg-purple-50' },
            ]?.map((s) => (
              <div key={s?.label} className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3">
                <div className={`w-10 h-10 ${s?.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>{s?.icon}</div>
                <div>
                  <p className="text-xl font-bold text-text-primary">{s?.value}</p>
                  <p className="text-xs text-text-secondary">{s?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-2xl border border-border p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-48">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  placeholder="Rechercher un asset ou propriété..."
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                />
              </div>

              {/* Property filter */}
              <select
                value={filterProperty}
                onChange={e => setFilterProperty(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              >
                <option value="">Toutes propriétés</option>
                {properties?.map(p => <option key={p?.id} value={p?.id}>{p?.title}</option>)}
              </select>

              {/* CDN filter */}
              <select
                value={filterCdn}
                onChange={e => setFilterCdn(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              >
                <option value="">Tous statuts CDN</option>
                <option value="cdn">CDN Optimisé</option>
                <option value="local">Non optimisé</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={e => setSortBy(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              >
                <option value="date_desc">Plus récent</option>
                <option value="date_asc">Plus ancien</option>
                <option value="name_asc">Nom A→Z</option>
                <option value="name_desc">Nom Z→A</option>
                <option value="size_desc">Taille ↓</option>
              </select>

              {/* View toggle */}
              <div className="flex items-center bg-surface rounded-xl p-1 gap-1">
                <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-brand-gold' : 'text-text-secondary hover:text-text-primary'}`}>
                  <Grid size={15} />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-brand-gold' : 'text-text-secondary hover:text-text-primary'}`}>
                  <List size={15} />
                </button>
              </div>
            </div>
          </div>

          {/* Batch actions bar */}
          {selectedIds?.length > 0 && (
            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button onClick={toggleSelectAll} className="p-1">
                  {allSelected ? <CheckSquare size={16} className="text-brand-gold" /> : <Square size={16} className="text-brand-gold" />}
                </button>
                <span className="text-sm font-medium text-brand-earth">{selectedIds?.length} asset(s) sélectionné(s)</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={handleBatchCdnOptimize} disabled={batchLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors">
                  <Zap size={13} /> Optimiser CDN
                </button>
                <button onClick={handleBatchDownload} disabled={batchLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
                  <Download size={13} /> Télécharger
                </button>
                <button onClick={handleBatchDelete} disabled={batchLoading} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors">
                  {batchLoading ? <Loader size={13} className="animate-spin" /> : <Trash2 size={13} />} Supprimer
                </button>
                <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 bg-white border border-border text-text-secondary text-xs font-medium rounded-lg hover:bg-surface transition-colors">
                  Désélectionner
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <Loader size={32} className="animate-spin text-brand-gold mx-auto mb-3" />
                <p className="text-text-secondary text-sm">Chargement des assets...</p>
              </div>
            </div>
          ) : filtered?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-border py-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-surface rounded-2xl flex items-center justify-center mb-4">
                <Package size={28} className="text-text-secondary opacity-50" />
              </div>
              <h3 className="font-semibold text-text-primary mb-1">{search || filterProperty || filterCdn ? 'Aucun résultat' : 'Aucun asset 3D'}</h3>
              <p className="text-sm text-text-secondary mb-5">
                {search || filterProperty || filterCdn ? 'Modifiez vos filtres pour voir plus de résultats' : 'Importez vos premiers modèles .obj pour commencer'}
              </p>
              {!search && !filterProperty && !filterCdn && (
                <button onClick={() => setUploadOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-gold hover:bg-brand-earth text-white text-sm font-medium rounded-xl transition-colors">
                  <Upload size={15} /> Importer des modèles .obj
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button onClick={toggleSelectAll} className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors">
                    {allSelected ? <CheckSquare size={14} className="text-brand-gold" /> : <Square size={14} />}
                    Tout sélectionner
                  </button>
                </div>
                <span className="text-xs text-text-secondary">{filtered?.length} asset(s)</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filtered?.map(asset => (
                  <AssetCard
                    key={asset?.id}
                    asset={asset}
                    selected={selectedIds?.includes(asset?.id)}
                    onSelect={toggleSelect}
                    onDelete={handleDelete}
                    onPreview={setPreviewAsset}
                    onCopyUrl={handleCopyUrl}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface">
                    <th className="px-4 py-3 text-left w-10">
                      <button onClick={toggleSelectAll}>
                        {allSelected ? <CheckSquare size={16} className="text-brand-gold" /> : <Square size={16} className="text-gray-300" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Fichier</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Propriété</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Géométrie</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">CDN</th>
                    <th className="px-4 py-3 text-left font-medium text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map(asset => (
                    <AssetRow
                      key={asset?.id}
                      asset={asset}
                      selected={selectedIds?.includes(asset?.id)}
                      onSelect={toggleSelect}
                      onDelete={handleDelete}
                      onPreview={setPreviewAsset}
                      onCopyUrl={handleCopyUrl}
                      onDownload={handleDownload}
                    />
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-border text-xs text-text-secondary">
                {filtered?.length} asset(s) affiché(s)
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      <PreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      <UploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} properties={properties} onUploaded={handleUploaded} />
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-lg text-sm font-medium transition-all ${toast?.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
          {toast?.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          {toast?.msg}
        </div>
      )}
    </>
  );
};

export default AssetManagementHub;
